import React, { useEffect, useState } from "react";
import { Collapse, List, Space } from "antd";
import { mapping } from "../base-requirements/requirements/fixtures/mapping.tsx";
import {
  Folder,
  Requirement,
  Subfolder,
  Type,
} from "../../redux/interfaces/folder.types.ts";
import {
  groupByStatus,
  groupBySubfolder,
} from "../../utils/base-requirements/requirements/groupByRequirements.ts";
import { useAppDispatch, useAppSelector } from "../../redux/hooks.ts";
import { useParams } from "react-router-dom";
import { fetchFolderRequirements } from "../../redux/actions/folderActions.tsx";
import { getSameLabelOrdinal } from "../../utils/subfolders/roleOrdinal.ts";

interface Props {
  folder: Folder;
}

const { Panel } = Collapse;

export const CollapseFolderExport: React.FC<Props> = ({ folder }) => {
  const { id } = useParams();
  const dispatch = useAppDispatch();

  const { groupType } = useAppSelector((state) => state.requirements);

  const [requirements, setRequirements] = useState<Requirement[]>([]);
  const [groupRequirements, setGroupRequirements] = useState<any>({});

  const subfolders = folder?.subfolders || [];

  const requirementsByStatus = groupByStatus(requirements);
  const requirementsBySubfolder = groupBySubfolder(subfolders);

  // In "Por subcarpeta" mode the group keys are subfolder ids (so several subfolders of
  // the same role render separately); resolve the header per subfolder, with the person
  // name as discriminator when present.
  const subfolderById: Record<string, Subfolder> = {};
  subfolders.forEach((sf) => {
    subfolderById[sf.id] = sf;
  });

  const subfolderBaseTitle = (sf: Subfolder): string =>
    mapping[sf.slug]?.title || sf.slug;

  const getGroupHeader = (group: string): string => {
    if (groupType === "subfolder") {
      const sf = subfolderById[group];
      if (sf) {
        const base = subfolderBaseTitle(sf);
        const fullName = sf.metadata?.full_name;
        if (fullName) return `${base} — ${fullName}`;
        // Number by role instead of `person_index`: DocVault numbers per-template,
        // so the principal and the first same-role additional can both be
        // person_index === 1 and would otherwise render identically.
        const ordinal = getSameLabelOrdinal(sf, subfolders, subfolderBaseTitle);
        if (ordinal) return `${base} ${ordinal}`;
        return base;
      }
    }
    return mapping[group]?.title || group;
  };

  useEffect(() => {
    handleFetchRequirements();
  }, [id]);

  useEffect(() => {
    handleGroupTypeChange();
  }, [groupType, requirements]);

  const handleGroupTypeChange = () => {
    if (groupType === "status") {
      setGroupRequirements(requirementsByStatus);
    }
    if (groupType === "subfolder") {
      setGroupRequirements(requirementsBySubfolder);
    }
  };

  const handleFetchRequirements = async () => {
    if (id) {
      const requirements =
        folder.subfolders?.flatMap(({ requirements }) => requirements) || [];
      setRequirements(requirements);
    } else {
      const requirements =
        (await dispatch(fetchFolderRequirements(folder.id)).unwrap()) || [];
      setRequirements(requirements);
    }
  };

  return (
    <Collapse
      className={`bg-white border-none w-full`}
      style={{ border: "none", maxHeight: "75vh", overflowY: "auto" }}
    >
      {Object.entries(groupRequirements).map(
        ([group, data]: [string, any], index) => (
          <Panel
            key={index}
            header={
              <Space>{`${getGroupHeader(group)} (${data.flat().length})`}</Space>
            }
            className={`border-none bg-white shadow-none h-fit`}
          >
            {data
              .filter((req: Requirement) => req.type !== Type.Form)
              .map((req: Requirement) => {
                const hasFiles = req.files.length > 0;

                return (
                  <Space
                    key={req.id}
                    className="w-full flex cursor-pointer border-none my-1"
                  >
                    <List.Item>
                      {req.name}
                      {!hasFiles && (
                        <span className="text-gray-600"> (Sin archivos)</span>
                      )}
                    </List.Item>
                  </Space>
                );
              })}
          </Panel>
        ),
      )}
    </Collapse>
  );
};
